import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Features = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "Your Dedicated AI Agent",
      description: "Every customer gets an AI agent assigned to their product. It studies your application, learns your business logic, and continuously generates quality test cases as your app evolves — like an SDET that never goes on leave."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: "Mobile App Automation",
      isNew: true,
      description: "No-code mobile app testing. Your agent taps, swipes, and validates like a real user — on real devices, on every build."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: "Jira & Test Tool Integration",
      description: "Connect Jira or any test management tool — QraftAI pulls your tickets, generates test cases, and runs them live on our platform. No copy-paste, no manual setup."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Reliable & Secure",
      description: "Enterprise-grade security, encryption in transit and at rest, and cloud infrastructure managed by experts — so your product is always available and always protected."
    }
  ];

  return (
    <section id="features" className="section-padding bg-gradient-to-br from-navy to-slate-900">
      <div className="container-max">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-poppins mb-6">
            Built Different. <span className="bg-gradient-to-r from-teal to-purple bg-clip-text text-transparent">Works Smarter.</span>
          </h2>
          <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto">
            Not another testing tool. An AI agent that joins your team — and never logs off.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="text-center group"
            >
              <div className="bg-gradient-to-br from-teal/20 to-purple/20 rounded-2xl p-6 mb-6 group-hover:from-teal/30 group-hover:to-purple/30 transition-all duration-300">
                <div className="text-teal group-hover:text-white transition-colors duration-300 flex justify-center">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold font-poppins mb-4 flex items-center justify-center gap-2 flex-wrap">
                {feature.title}
                {feature.isNew && (
                  <span className="bg-amber-400 text-navy text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider">NEW</span>
                )}
              </h3>
              <p className="text-white/80 leading-relaxed">
                {feature.description}
              </p>
              {feature.platforms && (
                <p className="text-teal/80 text-xs font-medium mt-3 tracking-wide">
                  {feature.platforms}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
